import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { useUiStore } from "@/stores/ui.store";
import { RentStatusBadge } from "@/components/RentStatusBadge";
import { Typography } from "@/components/ui/Typography";

export interface PropertyCardData {
  id: string;
  name: string;
  address: string;
  totalRent: number;
  units: {
    id: string;
    alias: string;
    unitNumber: string;
    dueDate?: number;
    tenants: { id: string; name: string }[];
  }[];
  rentStatus?: "PENDING" | "PAID" | "AWAITING_VERIFICATION" | "OVERDUE";
}

interface PropertyCardProps {
  property: PropertyCardData;
  onArchive?: () => void;
}

export function PropertyCard({ property, onArchive }: PropertyCardProps) {
  const { id, name, address, totalRent, units = [], rentStatus } = property;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const addToast = useUiStore((s) => s.addToast);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleArchive(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(false);
    if (!confirm("Archive this property? You can restore it later.")) return;
    try {
      await api.delete(`/properties/${id}`);
      addToast({ type: "success", message: "Property archived" });
      onArchive?.();
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to archive",
      });
    }
  }

  const unitSummary = units
    .map((u) => {
      const tenantNames = u.tenants.map((t) => t.name).join(", ");
      return `${u.alias}${tenantNames ? ` (${tenantNames})` : ""}`;
    })
    .join("; ");

  return (
    <div
      className="relative rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-4 sm:p-5 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none hover:border-primary-200 dark:hover:border-slate-600 transition-all cursor-pointer group"
      role="link"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("[data-card-actions]")) return;
        navigate(`/property/${id}`);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("[data-card-actions]")) return;
        navigate(`/property/${id}`);
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Typography variant="h3" className="truncate">{name}</Typography>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2">{address}</p>
          <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
            ₹{totalRent.toLocaleString("en-IN")}
            <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/month</span>
          </p>
          {units.length > 0 && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
              {units.length} unit{units.length > 1 ? "s" : ""}: {unitSummary}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0" data-card-actions>
          {rentStatus && <RentStatusBadge status={rentStatus} />}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              aria-label="Actions"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="6" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="18" r="1.5" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 w-40 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg dark:shadow-black/20 z-10">
                <button
                  type="button"
                  onClick={handleArchive}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  Archive property
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 items-center justify-between">
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {units.length} unit{units.length !== 1 ? "s" : ""}
        </span>
        <span className="text-xs font-medium text-primary-600 dark:text-primary-400 group-hover:underline">View details →</span>
      </div>
    </div>
  );
}
